import { Stack, Typography } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import { useState } from 'react';

import ConfirmationDialog from '@/components/dialogs/confirmationDialog';
import OptionMenu from '@/components/option-menu';
import { Panels } from '@/components/panels/panels';
import type { ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { createAllIncludedCondition } from '@/types/advanceFilter';

export const BackupScopeDynamicResourcesViewerHeader = ({
  resourcesScopeType,
  condition,
  onScopeChange,
  include,
  exclude,
}: {
  resourcesScopeType: ResourcesScopeType;
  condition?: Condition;
  onScopeChange: () => Promise<void>;
  include?: string[];
  exclude?: string[];
}) => {
  const { isAuthorized } = useRoles();
  const dal = useDAL();
  const [isDiscoverAllDialogOpen, setDiscoverAllDialogOpen] = useState(false);
  const { rightPanel, snackbar } = useWorkspace();

  const onDiscoverAllConfirm = async () => {
    await save();
    setDiscoverAllDialogOpen(false);
  };

  const save = async (
    condition: Condition = createAllIncludedCondition(),
    include: string[] = [],
    exclude: string[] = []
  ) => {
    await dal.inventory.backupScope.save({
      condition,
      include,
      exclude,
    });
    await onScopeChange();
  };

  const editResourceScope = () => {
    rightPanel.setComponent({
      panel: Panels.BackupScopeResourcesWizard,
      props: {
        condition,
        resourcesScopeType,
        include,
        exclude,
        onSave: async (condition, include, exclude) => {
          await save(condition, include, exclude);
          snackbar.showMessage('Resource scope updated');
        },
        onAbort: () => rightPanel.setIsOpen(false),
      },
    });
    rightPanel.setIsOpen(true, '100%');
  };

  return (
    <>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
      >
        <Typography variant='h4'>Dynamic resource selection</Typography>
        <OptionMenu
          iconClassName='text-textPrimary'
          options={[
            {
              text: 'Edit',
              menuItemProps: {
                onClick: editResourceScope,
                disabled: !isAuthorized('update:backup_scope'),
              },
              testId: 'edit-dynamic-resources',
            },
            {
              text: 'Discover all',
              menuItemProps: {
                onClick: () => setDiscoverAllDialogOpen(true),
                disabled:
                  resourcesScopeType === 'allIncluded' ||
                  !isAuthorized('update:backup_scope'),
              },
              testId: 'reset-dynamic-resources',
            },
          ]}
        />
      </Stack>
      <ConfirmationDialog
        isOpen={isDiscoverAllDialogOpen}
        onCancel={() => setDiscoverAllDialogOpen(false)}
        onConfirm={onDiscoverAllConfirm}
        confirmSnackbarText='Discovering all resources'
        title='Discover all resources'
        description='Eon will discover automatically all resources from your connected sources. When new ones are added, they’ll be included too.'
        buttonTitle='Discover All'
      />
    </>
  );
};
